import * as React from 'react';
import { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  Skeleton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import GarageIcon from '@mui/icons-material/Garage';
import { withTranslation } from 'react-i18next';
import ServiceBayFormDialog from './ServiceBayFormDialog';

// Service Bay Card Component
function ServiceBayCard({ serviceBay, t, onClick }) {
  return (
    <Paper
      elevation={0}
      onClick={onClick}
      sx={{
        p: 3,
        height: '100%',
        cursor: 'pointer',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          transform: 'translateY(-2px)',
        },
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        {/* Icon */}
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: '12px',
            bgcolor: 'rgba(49, 130, 206, 0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'secondary.main',
          }}
        >
          <GarageIcon />
        </Box>
        {/* Status Badge */}
        <Box
          sx={{
            px: 1.25,
            py: 0.5,
            borderRadius: '20px',
            fontSize: '12px',
            fontWeight: 500,
            bgcolor: serviceBay.is_active ? '#e6ffed' : '#fee2e2',
            color: serviceBay.is_active ? '#48bb78' : '#fc8181',
          }}
        >
          {serviceBay.is_active ? t('serviceBays.active') : t('serviceBays.inactive')}
        </Box>
      </Box>

      {/* Service Bay Name */}
      <Typography
        sx={{
          fontSize: '16px',
          fontWeight: 600,
          color: 'text.primary',
          mb: 0.5,
          fontFamily: "'Plus Jakarta Sans', sans-serif",
        }}
      >
        {serviceBay.name}
      </Typography>

      {/* Description */}
      {serviceBay.description && (
        <Typography
          sx={{
            fontSize: '14px',
            color: 'text.secondary',
            mb: 2,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {serviceBay.description}
        </Typography>
      )}

      {/* Display Order */}
      <Box
        sx={{
          p: 1.5,
          px: 2,
          bgcolor: 'background.default',
          borderRadius: '8px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mt: 'auto',
        }}
      >
        <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>
          {t('serviceBays.displayOrder')}
        </Typography>
        <Typography
          sx={{
            fontSize: '18px',
            fontWeight: 700,
            color: 'text.primary',
            fontFamily: "'Plus Jakarta Sans', sans-serif",
          }}
        >
          {serviceBay.display_order}
        </Typography>
      </Box>
    </Paper>
  );
}

function MainServiceBaysView({ t }) {
  const [serviceBays, setServiceBays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedServiceBay, setSelectedServiceBay] = useState(null);

  const fetchServiceBays = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/scheduling/service-bays/');
      // Handle both paginated and non-paginated responses
      const data = response.data.results || response.data;
      setServiceBays(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchServiceBays();
  }, []);

  const handleServiceBayCreated = (newServiceBay) => {
    setServiceBays((prev) => [...prev, newServiceBay].sort((a, b) => {
      if (a.display_order !== b.display_order) {
        return a.display_order - b.display_order;
      }
      return a.name.localeCompare(b.name);
    }));
  };

  const handleServiceBayClick = (serviceBay) => {
    setSelectedServiceBay(serviceBay);
    setDialogOpen(true);
  };

  const handleServiceBayUpdated = () => {
    fetchServiceBays();
    setSelectedServiceBay(null);
  };

  const handleServiceBayDeleted = () => {
    fetchServiceBays();
    setSelectedServiceBay(null);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedServiceBay(null);
  };

  if (error) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="error">{t('Error')}: {error}</Typography>
      </Box>
    );
  }

  return (
    <Box data-testid="service-bays-page">
      {/* Header - Responsive */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: 2,
          mb: 3,
        }}
      >
        <Box>
          <Typography
            component="h4"
            variant="h4"
            sx={{
              fontWeight: 700,
              color: 'text.primary',
              fontFamily: "'Plus Jakarta Sans', sans-serif",
            }}
          >
            {t('serviceBays.title')}
          </Typography>
          <Typography
            sx={{
              color: 'text.secondary',
              fontSize: '14px',
              mt: 0.5,
            }}
          >
            {t('serviceBays.description')}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setDialogOpen(true)}
          data-testid="add-service-bay-button"
          sx={{
            bgcolor: 'secondary.main',
            color: 'white',
            px: 2.5,
            py: 1.5,
            borderRadius: '10px',
            fontWeight: 500,
            fontSize: '14px',
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            boxShadow: '0 4px 12px rgba(49, 130, 206, 0.3)',
            '&:hover': {
              bgcolor: 'secondary.light',
              boxShadow: '0 6px 16px rgba(49, 130, 206, 0.4)',
            },
          }}
        >
          {t('serviceBays.addServiceBay')}
        </Button>
      </Box>

      {/* Service Bay Cards Grid */}
      {loading ? (
        <Grid container spacing={2.5}>
          {[1, 2, 3].map((i) => (
            <Grid item xs={12} sm={6} md={4} key={i}>
              <Skeleton variant="rectangular" height={180} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
      ) : serviceBays.length === 0 ? (
        <Paper elevation={0} sx={{ p: 4, textAlign: 'center' }}>
          <GarageIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
            {t('serviceBays.noServiceBays')}
          </Typography>
          <Typography color="text.secondary">
            {t('serviceBays.noServiceBaysHint')}
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={2.5}>
          {serviceBays.map((serviceBay) => (
            <Grid item xs={12} sm={6} md={4} key={serviceBay.id}>
              <ServiceBayCard
                serviceBay={serviceBay}
                t={t}
                onClick={() => handleServiceBayClick(serviceBay)}
              />
            </Grid>
          ))}
        </Grid>
      )}

      <ServiceBayFormDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        serviceBay={selectedServiceBay}
        onCreated={handleServiceBayCreated}
        onUpdated={handleServiceBayUpdated}
        onDeleted={handleServiceBayDeleted}
      />
    </Box>
  );
}

export default withTranslation()(MainServiceBaysView);
